import { useQueries, useQuery } from '@tanstack/react-query';

import { type WishlistItem } from '@app/client/common/structs/wishlists/wishlists';
import { callGetCollectionMetadataDetails } from '@app/client/resources/collections/collection';
import { callGetTokenDetailsList } from '@app/client/resources/marketplace';
import { checkValidContractAddress, getMashedUrl } from '@app/helpers';

type UseWishlistThumbnailsProps = {
    items?: WishlistItem[];
    assets?: string[];
};
export const useWishlistThumbnails = ({ items, assets = [] }: UseWishlistThumbnailsProps) => {
    const collections: string[] = [];
    const tokenDetailsProps: { [key: string]: string[] } = {};
    const tokensCache = ['thumbnailTokens'];

    items?.forEach((item) => {
        const token = item.item.Token;
        const collection = item.item.Collection;
        if (token) {
            const { contract, token_id } = token;
            if (contract && token_id) {
                if (!tokenDetailsProps[contract]) tokenDetailsProps[contract] = [];
                tokenDetailsProps[contract].push(token_id);
                tokensCache.push(`${contract}:${token_id}`);
            }
        } else if (collection) {
            collections.push(collection.slug);
        }
    }, {});

    const { data: tokensData } = useQuery(
        tokensCache,
        ({ signal }) =>
            // TODO: handle fallbacks
            callGetTokenDetailsList({
                tokens: Object.keys(tokenDetailsProps).map((contractAddress) => ({
                    contract: checkValidContractAddress(contractAddress),
                    token_ids: tokenDetailsProps[contractAddress],
                })),
                settings: { signal },
            }),
        {
            retry: false,
            keepPreviousData: true,
            enabled: !assets?.length && tokensCache.length > 1,
        },
    );

    const collectionsResponse = useQueries({
        queries: collections.map((slug) => ({
            queryKey: ['collectionMetadata', slug],
            retry: false,
            keepPreviousData: true,
            queryFn: ({ signal }: { signal?: AbortSignal }) =>
                callGetCollectionMetadataDetails({
                    slug: slug || '',
                    query: {
                        includeAttributes: true,
                        includeOwnerCount: true,
                        includeSalesCount: true,
                    },
                    settings: {
                        signal,
                    },
                }),
        })),
    });

    const thumbnailAssets: string[] = [];
    if (tokensData?.ok && tokensData.value) {
        tokensData.value.contents?.forEach((token) => {
            token.image_url && thumbnailAssets.push(token.image_url);
        });
    }

    collectionsResponse.forEach((response) => {
        if (response.data) {
            thumbnailAssets.push(response.data.collection?.thumbnail);
        }
    });

    return {
        thumbnailAssets: assets.length
            ? assets
            : thumbnailAssets.map((thumbnail) => getMashedUrl({ url: thumbnail, width: 500 })),
    };
};
